/**
 * Exam Module Configuration
 * Defines navigation, routes, and features for the Exam/Quiz module.
 */

import { lazy } from 'react';
import type { ModuleConfig } from '../types';
import SchoolIcon from '@mui/icons-material/School';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List';

// Lazy load exam pages
const ExamList = lazy(() => import('@/pages/ExamList'));
const ExamBuilder = lazy(() => import('@/pages/ExamBuilder/ExamBuilderMain'));
const ExamAttempts = lazy(() => import('@/pages/ExamAttempts'));
const ExamResults = lazy(() => import('@/pages/ExamResults'));
const ExamGrading = lazy(() => import('@/pages/ExamGrading'));
const ExamTaking = lazy(() => import('@/pages/ExamTaking'));
const PublicExam = lazy(() => import('@/pages/PublicExam'));
const PublicExamResults = lazy(() => import('@/pages/PublicExamResults'));

export const examModuleConfig: ModuleConfig = {
  code: 'exam',
  name: 'Exams/Quizzes',
  description: 'Create and manage exams and quizzes with grading and analytics',
  version: '1.0.0',
  enabledByDefault: false, // Backend controls this via API
  icon: SchoolIcon,
  themeColor: '#1F2A44',
  order: 2,

  navItems: [
    {
      id: 'exams',
      labelKey: 'navigation.exams',
      path: '/exams',
      icon: SchoolIcon,
      order: 20,
      requiresAuth: true,
    },
  ],

  routes: [
    {
      path: '/exams',
      component: ExamList,
      requiresAuth: true,
      title: 'Exams',
    },
    {
      path: '/exams/new',
      component: ExamBuilder,
      requiresAuth: true,
      title: 'Create Exam',
    },
    {
      path: '/exams/:id/edit',
      component: ExamBuilder,
      requiresAuth: true,
      title: 'Edit Exam',
    },
    {
      path: '/exams/:examId/attempts',
      component: ExamAttempts,
      requiresAuth: true,
      title: 'Exam Attempts',
    },
    {
      path: '/exams/:examId/results',
      component: ExamResults,
      requiresAuth: true,
      title: 'Exam Results',
    },
    {
      path: '/exams/:examId/grade',
      component: ExamGrading,
      requiresAuth: true,
      title: 'Grade Exam',
    },
    {
      path: '/exams/:examId/take',
      component: ExamTaking,
      requiresAuth: true,
      title: 'Take Exam',
    },
    {
      path: '/e/:token',
      component: PublicExam,
      requiresAuth: false,
      layout: 'full',
      title: 'Take Exam',
    },
    {
      path: '/e/:token/results',
      component: PublicExamResults,
      requiresAuth: false,
      layout: 'full',
      title: 'Exam Results',
    },
  ],

  quickActions: [
    {
      id: 'create-exam',
      labelKey: 'navigation.newExam',
      icon: AddIcon,
      path: '/exams/new',
      order: 3,
      variant: 'primary',
    },
    {
      id: 'view-exams',
      labelKey: 'navigation.exams',
      icon: ListIcon,
      path: '/exams',
      order: 4,
      variant: 'outlined',
    },
  ],
};
